// src/player/PhotoGridBlock.tsx
// Figma "Shared / Wall / Photo grid" (4257:2167): full-bleed, square corners, 2px gaps, 3:2 frame; one big tile
// (2/3 width) beside two stacked; 4+ items show "+N" on the third tile. 1-2 items use the same frame.
import { useTranslation } from "react-i18next";
import { Play } from "lucide-react";
import type { WallItem } from "../api/content";
import { formatDuration } from "./formatDuration";
import { isPhotoItem, isVideoItem } from "./registry";
import Img from "../components/ui/Img";

export const GRID_MAX_TILES = 3;

interface Props {
  items: WallItem[];
  /** Index within `items` of the tile to open (the "+N" tile reports the first hidden photo). */
  onOpen(i: number): void;
}

export default function PhotoGridBlock({ items, onOpen }: Props) {
  const { t } = useTranslation();
  const shown = items.slice(0, GRID_MAX_TILES);
  const hidden = items.length - shown.length;
  if (shown.length === 0) return null;

  // "+N" opens at the first hidden photo (never a video), else at the tile itself.
  const moreAt = (() => {
    const j = items.findIndex((it, i) => i >= GRID_MAX_TILES && isPhotoItem(it));
    return j >= 0 ? j : shown.length - 1;
  })();

  function tile(i: number, cls: string) {
    const it = shown[i];
    const video = isVideoItem(it);
    const more = hidden > 0 && i === shown.length - 1;
    const src = video ? it.thumbnailUrl : it.photoUrl;
    const dur = video ? formatDuration(it.durationMs) : null;
    return (
      <button
        key={it.id}
        type="button"
        data-testid={`wall-photo-${it.id}`}
        aria-label={video && !more ? t("wall_open_video", "Open video") : t("wall_open_photo")}
        className={`relative block overflow-hidden bg-[var(--bg-elevated)] ${cls}`}
        onClick={() => onOpen(more ? moreAt : i)}
      >
        {src && <Img src={src} alt="" className="absolute inset-0 w-full h-full object-cover" />}
        {more ? (
          <>
            <span className="absolute inset-0 bg-black/50" />
            <span data-testid="wall-photo-more" className="absolute inset-0 flex items-center justify-center text-xl font-bold text-white">
              {t("wall_more_photos", "+{{count}}", { count: hidden })}
            </span>
          </>
        ) : (
          video && (
            <>
              <span data-testid="video-play-badge" className="absolute inset-0 m-auto w-10 h-10 rounded-full bg-black/55 text-white flex items-center justify-center">
                <Play size={22} aria-hidden="true" />
              </span>
              {dur && <span className="absolute right-1.5 bottom-1.5 rounded bg-black/65 px-1.5 py-0.5 text-xs text-white">{dur}</span>}
            </>
          )
        )}
      </button>
    );
  }

  return (
    <div data-testid="wall-photo-grid" className="flex gap-[2px] w-full" style={{ aspectRatio: "3 / 2" }}>
      {shown.length === 1 && tile(0, "flex-1")}
      {shown.length === 2 && (
        <>
          {tile(0, "flex-1")}
          {tile(1, "flex-1")}
        </>
      )}
      {shown.length >= 3 && (
        <>
          {tile(0, "flex-[2]")}
          <div className="flex flex-col gap-[2px] flex-1">
            {tile(1, "flex-1")}
            {tile(2, "flex-1")}
          </div>
        </>
      )}
    </div>
  );
}
